import { Link } from 'react-router-dom';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import { services } from '@/mocks/services';
import { CREDIT_COPY, DEFAULT_DISCLAIMER, UNPUBLISHED_RANGE, formatRange, formatUsd, usePublicPricing } from '@/lib/pricing';

// Reads only the published public pricing source. Unpublished values show honest fallback copy.
export default function PublishedRanges() {
  const { state, data, priceFor, reload } = usePublicPricing();
  const fee = data?.diagnostic_fee;
  return (
    <section className="bg-background-50 pb-20 md:pb-28">
      <Container>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16">
          <Eyebrow label="Typical Repair Costs" />
          <div>
            <h2 className="text-[30px] md:text-[44px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">What repairs typically cost.</h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-foreground-600">{data?.disclaimer || DEFAULT_DISCLAIMER}</p>
          </div>
        </div>

        <div className="mt-12">
          {state === 'error' && (
            <p className="border-t border-background-200 py-6 text-sm text-foreground-600">
              Pricing couldn&apos;t be loaded.{' '}
              <button type="button" onClick={reload} className="cursor-pointer whitespace-nowrap underline">Retry</button>
            </p>
          )}
          {services.map((s) => {
            const p = state === 'ready' ? priceFor(s.slug) : null;
            return (
              <div key={s.slug} className="grid grid-cols-1 gap-2 border-t border-background-200 py-5 last:border-b sm:grid-cols-12 sm:items-center sm:gap-6">
                <Link to={`/services/${s.slug}`} className="sm:col-span-6 text-base text-foreground-950 hover:text-primary-600">{s.name}</Link>
                <p className="sm:col-span-6 sm:text-right text-[15px] text-foreground-950">
                  {state === 'loading' && <span className="text-foreground-600">Loading…</span>}
                  {state === 'ready' && (p ? formatRange(p) : <span className="text-sm text-foreground-600">{UNPUBLISHED_RANGE}</span>)}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="border-l-2 border-primary-200 pl-5">
            <p className="text-xs uppercase tracking-[0.12em] text-foreground-600">Diagnostic visit</p>
            <p className="mt-2 text-lg text-foreground-950">
              {fee ? (fee.amount_cents > 0 ? formatUsd(fee.amount_cents) : 'No diagnostic visit fee') : 'Shared with you before your visit is confirmed'}
            </p>
            {fee?.explanation && <p className="mt-2 text-sm leading-relaxed text-foreground-600">{fee.explanation}</p>}
            {fee && fee.amount_cents > 0 && fee.credited_toward_repair && <p className="mt-2 text-sm leading-relaxed text-foreground-700">{CREDIT_COPY}</p>}
          </div>
          <div className="border-l-2 border-background-200 pl-5">
            <p className="text-xs uppercase tracking-[0.12em] text-foreground-600">Final quote</p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">Ranges are a guide, not a quote. After diagnosing your appliance, the technician gives you a final repair quote, and no repair work starts without your approval.</p>
            <Link to="/book" className="mt-5 inline-flex h-11 items-center whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm text-white hover:bg-primary-600">Book a Repair</Link>
          </div>
        </div>
      </Container>
    </section>
  );
}